import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Logger } from '../../../shared/services/logger.service';

interface GuestWorkplaceState {
  guest: {
    guest_pid: string;
    name: string | null;
    email: string | null;
    host_moniker: string | null;
    roles: string[];
  } | null;

  currentRoom: {
    pid: string | null;
    name: string | null;
    room_number: string | null;
    background_url?: string;
  };

  currentFloor: {
    pid: string | null;
    name: string | null;
  };

  org: {
    pid: string | null;
    name: string | null;
    slug: string | null;
  };

  floorPresence: {
    users: Array<{
      pid: string;
      name: string;
      moniker: string;
      avatar_url: string | null;
      avatar_thumbnail_url?: string | null;
      room: string;
    }>;
    guests: Array<{
      pid: string;
      name: string;
      moniker: string;
      avatar_url: string | null;
      avatar_thumbnail_url?: string | null;
      room: string;
    }>;
  } | null;

  officeHierarchy: Array<{
    pid: string;
    location: string;
    buildings: Array<{
      pid: string;
      name: string;
      address: string;
      imageUrl: string | null;
      floors: Array<{
        pid: string;
        name: string;
        image_url: string;
        available: number;
        away: number;
        locked: number;
      }>;
    }>;
  }> | null;

  floorPlace: {
    pid: string;
    name: string;
    image_url: string;
    rooms: Array<{
      pid: string;
      name: string;
      number?: string;
      type: string;
      status: string;
      coordinates: any;
      background_url?: string;
      displayName?: string;
      ooo_message?: string;
    }>;
  } | null;

  isInitialized: boolean;
  isLoading: boolean;
  error: string | null;

  initFromBootstrap: (data: any) => void;
  setGuest: (guest: GuestWorkplaceState['guest']) => void;
  setCurrentRoom: (room: GuestWorkplaceState['currentRoom']) => void;
  setCurrentFloor: (floor: GuestWorkplaceState['currentFloor']) => void;
  setOrg: (org: GuestWorkplaceState['org']) => void;
  setFloorPresence: (floorPresence: GuestWorkplaceState['floorPresence']) => void;
  setOfficeHierarchy: (officeHierarchy: GuestWorkplaceState['officeHierarchy']) => void;
  setFloorPlace: (floorPlace: GuestWorkplaceState['floorPlace']) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setInitialized: (initialized: boolean) => void;
  reset: () => void;
  clearPersistedState: () => void;
}

const initialState = {
  guest: null,
  currentRoom: {
    pid: null,
    name: 'Lobby',
    room_number: null,
  },
  currentFloor: {
    pid: null,
    name: 'Main Floor',
  },
  org: {
    pid: null,
    name: null,
    slug: null,
  },
  floorPresence: null,
  officeHierarchy: null,
  floorPlace: null,
  isInitialized: false,
  isLoading: false,
  error: null,
};

export const useGuestWorkplaceStore = create<GuestWorkplaceState>()(
  persist(
    (set) => ({
      ...initialState,

      initFromBootstrap: (data: any) => {
        Logger.debug('[GuestWorkplaceStore] 🔄 Initializing from bootstrap data:', data);

        // Store orgPid in localStorage for API calls
        if (data.org?.pid) {
          localStorage.setItem('orgPid', data.org.pid);
        }

        set({
          guest: data.guest || null,
          org: data.org || initialState.org,
          currentRoom: {
            pid: data.presence?.room_pid || null,
            name: data.presence?.room_name || 'Lobby',
            room_number: data.presence?.room_number || null,
            background_url: data.presence?.background_url,
          },
          currentFloor: {
            pid: data.presence?.floor_pid || null,
            name: data.presence?.floor_name || 'Main Floor',
          },
          isInitialized: true,
          isLoading: false,
          error: null,
        });

        Logger.debug('[GuestWorkplaceStore] ✅ Bootstrap initialization complete');
      },

      setGuest: (guest) => set({ guest }),

      setCurrentRoom: (room) => set({ currentRoom: room }),

      setCurrentFloor: (floor) => set({ currentFloor: floor }),

      setOrg: (org) => set({ org }),

      setFloorPresence: (floorPresence) => {
        Logger.debug('[GuestWorkplaceStore] 🏢 Setting floor presence:', floorPresence);
        set({ floorPresence });
      },

      setOfficeHierarchy: (officeHierarchy) => {
        Logger.debug('[GuestWorkplaceStore] 🏢 Setting office hierarchy:', officeHierarchy);
        set({ officeHierarchy });
      },

      setFloorPlace: (floorPlace) => {
        Logger.debug('[GuestWorkplaceStore] 🗺️ Setting floor place:', floorPlace);
        set({ floorPlace });
      },

      setLoading: (loading) => set({ isLoading: loading }),

      setError: (error) => {
        Logger.debug('[GuestWorkplaceStore] ❌ Setting error:', error);
        set({ error, isLoading: false });
      },

      setInitialized: (initialized) => set({ isInitialized: initialized }),

      reset: () => {
        Logger.debug('[GuestWorkplaceStore] 🔄 Resetting guest workplace state');
        set(initialState);
      },

      clearPersistedState: () => {
        Logger.debug('[GuestWorkplaceStore] 🧹 Clearing persisted state and resetting');
        localStorage.removeItem('guest-workplace-store');
        set(initialState);
      },
    }),
    {
      name: 'guest-workplace-store',
      partialize: (state) => ({
        // Persist all important state fields, exclude temporary states
        guest: state.guest,
        currentRoom: state.currentRoom,
        currentFloor: state.currentFloor,
        org: state.org,
        floorPresence: state.floorPresence,
        floorPlace: state.floorPlace,
        officeHierarchy: state.officeHierarchy,
        isInitialized: state.isInitialized,
        // Note: isLoading and error are not persisted as they are temporary states
      }),
      // Add version control for future migrations
      version: 1,
      // Optional: Custom storage key prefix
      storage: {
        getItem: (name) => {
          try {
            const item = localStorage.getItem(name);
            return item ? JSON.parse(item) : null;
          } catch (error) {
            console.warn('[GuestWorkplaceStore] Failed to get item from localStorage:', error);
            return null;
          }
        },
        setItem: (name, value) => {
          try {
            localStorage.setItem(name, JSON.stringify(value));
          } catch (error) {
            console.warn('[GuestWorkplaceStore] Failed to set item in localStorage:', error);
          }
        },
        removeItem: (name) => {
          try {
            localStorage.removeItem(name);
          } catch (error) {
            console.warn('[GuestWorkplaceStore] Failed to remove item from localStorage:', error);
          }
        },
      },
    }
  )
);
